import React from "react";
import {
  CHUNK_RELOAD_STORAGE_KEY,
  isChunkLoadError,
  reloadOnceForChunkError,
} from "@/utils/lazyWithChunkReload";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback: React.ReactNode | ((reset: () => void) => React.ReactNode);
}

interface ErrorBoundaryState {
  hasError: boolean;
  /** True while a chunk-miss reload is in flight — avoid flashing ErrorView. */
  reloadingForChunk: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, reloadingForChunk: false };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Only suppress the fallback when we are about to reload. If a prior
    // reload already left the session flag set, show ErrorView instead of
    // hanging on a blank screen.
    let willReload = false;
    if (isChunkLoadError(error)) {
      try {
        willReload = sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) == null;
      } catch {
        willReload = false;
      }
    }
    return { hasError: true, reloadingForChunk: willReload };
  }

  componentDidCatch(error: Error) {
    if (this.state.reloadingForChunk) {
      reloadOnceForChunkError(error);
    }
  }

  reset = () => {
    this.setState({ hasError: false, reloadingForChunk: false });
  };

  render() {
    if (this.state.reloadingForChunk) {
      return null;
    }
    if (this.state.hasError) {
      const { fallback } = this.props;
      return typeof fallback === "function" ? fallback(this.reset) : fallback;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
